import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2, X, ImageIcon, Pencil, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const SubPhotoGallery = ({ projectId, subId, user, subName }) => {
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [editingDesc, setEditingDesc] = useState(false);
  const [editText, setEditText] = useState('');

  const canUpload = user.role === 'admin' || user.role === 'supervisor' || user.role === 'employee';
  const canDelete = user.role === 'admin' || user.role === 'supervisor';

  const fetchPhotos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/subcontractors/${subId}/items`);
      setPhotos(r.data.filter((i) => i.type === 'photo'));
    } catch (e) { /* not authorized or none */ }
  }, [projectId, subId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const uploadPhotos = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      if (description.trim()) fd.append('description', description.trim());
      await axios.post(`${API}/projects/${projectId}/subcontractors/${subId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiles([]);
      setDescription('');
      toast.success(`${files.length} photo${files.length !== 1 ? 's' : ''} uploaded — ${subName || 'the sub-contractor'} has been notified`);
      fetchPhotos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload photos');
    } finally { setUploading(false); }
  };

  const deletePhoto = async (itemId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/subcontractors/${subId}/items/${itemId}`);
      toast.success('Photo deleted');
      setViewer(null);
      fetchPhotos();
    } catch (e) { toast.error('Failed to delete photo'); }
  };

  const saveDescription = async () => {
    try {
      const r = await axios.put(`${API}/projects/${projectId}/subcontractors/${subId}/items/${viewer.id}`, { text: editText });
      toast.success('Description updated');
      setViewer({ ...viewer, text: r.data.text });
      setEditingDesc(false);
      fetchPhotos();
    } catch (e) { toast.error('Failed to update description'); }
  };

  return (
    <>
    <div className="glass-card p-4 sm:p-6" data-testid="sub-photo-gallery">
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Camera className="w-5 h-5 text-orange-400" />
        {subName ? `Photos for ${subName}` : 'Your Photo Gallery'}
      </h3>

      {canUpload && (
        <div className="space-y-2 p-3 rounded-lg bg-gray-100 border border-gray-200 mb-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="flex-1 bg-white border-2 border-slate-300 text-slate-900 text-sm"
              data-testid="sub-gallery-file-input"
            />
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
              className="flex-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm"
              data-testid="sub-gallery-desc-input"
            />
            <Button
              onClick={uploadPhotos}
              disabled={uploading || files.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white min-h-[44px]"
              data-testid="sub-gallery-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Camera className="w-4 h-4 mr-2" />Upload{files.length > 0 ? ` (${files.length})` : ''}</>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500">They&apos;ll get an in-app alert when you upload. Clients never see these photos.</p>
        </div>
      )}

      {photos.length === 0 ? (
        <div className="text-center py-10 text-gray-400" data-testid="sub-gallery-empty">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No photos yet{canUpload ? ' — upload the first one above' : ''}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <button
              key={p.id}
              onClick={() => setViewer(p)}
              className="relative group rounded-lg overflow-hidden aspect-square border border-gray-200 bg-slate-800"
              data-testid={`sub-gallery-photo-${p.id}`}
            >
              <img
                src={`${BACKEND_URL}${p.file_path}`}
                alt={p.text || 'Photo'}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
              {p.text && (
                <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs px-2 py-1 truncate text-left">{p.text}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>

      {viewer && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setViewer(null)}
          data-testid="sub-gallery-viewer"
        >
          <div className="max-w-4xl w-full max-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end gap-2 mb-2">
              {canDelete && (
                <>
                <Button size="sm" variant="ghost" onClick={() => { setEditText(viewer.text || ''); setEditingDesc(true); }} style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', color: '#60a5fa' }} data-testid="sub-gallery-edit-btn">
                  <Pencil className="w-5 h-5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deletePhoto(viewer.id)} style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', color: '#f87171' }} data-testid="sub-gallery-delete-btn">
                  <Trash2 className="w-5 h-5" />
                </Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => { setEditingDesc(false); setViewer(null); }} style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', color: '#ffffff' }} data-testid="sub-gallery-close-btn">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <img src={`${BACKEND_URL}${viewer.file_path}`} alt={viewer.text || 'Photo'} className="w-full max-h-[70vh] object-contain rounded-lg" />
            <div
              className="mt-3 mx-auto px-4 py-2 rounded-lg text-center"
              style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', maxWidth: '92%' }}
            >
              {editingDesc ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    placeholder="Photo description..."
                    className="flex-1 bg-white border-2 border-slate-300 text-slate-900 text-sm"
                    data-testid="sub-gallery-edit-input"
                  />
                  <Button size="sm" onClick={saveDescription} className="bg-green-600 hover:bg-green-700" style={{ color: '#ffffff' }} data-testid="sub-gallery-save-desc-btn">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingDesc(false)} className="border-slate-400">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  {viewer.text && <p className="text-base" style={{ color: '#ffffff' }}>{viewer.text}</p>}
                  <p className="text-sm mt-1" style={{ color: '#cbd5e1' }}>
                    From {viewer.uploaded_by_name} · {new Date(viewer.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubPhotoGallery;
